import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Spinner from '../ui-elements/Spinner'
import { subscribe } from '../utils/pubsub'
import ProcessSummaryCheckPeriods from './ProcessSummaryCheckPeriods'
import YellowAlert from '../ui-elements/YellowAlert'
import ProcessStageIndicator from './ProcessStageIndicator'
import FooterNavButtonFrame from '../shared-components/FooterNavButtonFrame'
import StartProcess from './StartProcess'



type Props = {
}


// countdown timer compoentent - gets given time of next check in milliseconds as property and 
// counts down in minutes, updating every 20 seconds or so

function CountdownTimer({
    timeToNextCheck
}: { timeToNextCheck: number }) {

    const [minutesToNextCheck, setMinutesToNextCheck] = useState<number | null>(null)
    const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null)
    const { t } = useContext(GlobalContext)

    useEffect(() => {
        if (timerId) {
            clearInterval(timerId)
        }
        const myTimerId = setInterval(() => {
            updateMinutesToNextCheck(timeToNextCheck)
        }, 20000)

        setTimerId(myTimerId)
        updateMinutesToNextCheck(timeToNextCheck)

        return () => {
            timerId && clearInterval(timerId)
        }
    }, [timeToNextCheck])

    function updateMinutesToNextCheck(timestamp_utc_milliseconds: number) {
        //  local time might not be utc, need to get time in utc timezone
        //const now = new Date().getTime() + new Date().getTimezoneOffset() * 60000
        const now = Date.now()
        const minutes = Math.floor((timestamp_utc_milliseconds - now) / 60000)
        setMinutesToNextCheck(minutes)
    }



    //cancel timer when component unmounts

    if (minutesToNextCheck && minutesToNextCheck > 1) {
        return <YellowAlert fullwidth={true}>{t('ProcessPages.ProcessSummary.NextChecksDueInMinutes', { minutes: minutesToNextCheck })}</YellowAlert>
    } else {
        return <YellowAlert fullwidth={true}>{t('ProcessPages.ProcessSummary.NextChecksDueSoon')}</YellowAlert>

    }
}

function ProcessSummary({
}: Props) {

    const { processId } = useParams()
    const navigate = useNavigate()
    const {
        tableData,
        sendMessageToWebsocket,
        setShowModal,
        processData,
        t
    } = useContext(GlobalContext)
    const [pendingProcessDefinitionIds, setPendingProcessDefinitionIds] = useState<string[]>([])
    const [requestedProcessDefinitionIds, setRequestedProcessDefinitionIds] = useState<string[]>([])

    useEffect(() => {
        if (
            tableData && processId &&
            (!tableData.ProcessDefinitions || !tableData.ProcessDefinitions[processId])) {
            setPendingProcessDefinitionIds([...pendingProcessDefinitionIds, processId])
        }
    }, [tableData])


    useEffect(() => {
        if (pendingProcessDefinitionIds && pendingProcessDefinitionIds.length > 0) {
            for (const processId of pendingProcessDefinitionIds) {
                if (!requestedProcessDefinitionIds.includes(processId)) {
                    fetchProcessDefinition(processId)
                    setRequestedProcessDefinitionIds([...requestedProcessDefinitionIds, processId])
                }
            }
        }
    }, [pendingProcessDefinitionIds])

    const fetchProcessDefinition = (processId: string) => {
        let requestAssets = JSON.stringify({
            action: "processesClient",
            subAction: "fetchProcessDefinition",
            processId: processId
        })
        sendMessageToWebsocket(requestAssets)
    }


    const completeStageCustomNextStep = (nextStageId: string, processRecordId: string) => {
        setShowConfirmEndStage(false)
        setShowModal({ 'spinner': t('General.loading') })
        let payload = JSON.stringify({
            action: "processesClient",
            subAction: "completeStageCustomNextStep",
            nextStageId: nextStageId,
            processRecordId: processRecordId
        })

        const unsubscribeUpdate = subscribe("completeStageCustomNextStepResponse", data => {
            if (data && data.processRecordId === processRecordId) {
                setShowModal(null)
                if (data.nextChecklistPeriod) {
                    navigate(`/checklist/${data.nextChecklistPeriod}`)
                }
            }
            unsubscribeUpdate()
        })
        sendMessageToWebsocket(payload)
    }

    const completeStageDefault = (processRecordId: string) => {
        setShowConfirmEndStage(false)
        setShowModal({ 'spinner': t('General.loading') })
        let payload = JSON.stringify({
            action: "processesClient",
            subAction: "completeStageDefaultNextStep",
            processRecordId: processRecordId,
            currentStageId: currentStageId
        })

        const unsubscribeUpdate = subscribe("completeStageDefaultNextStepResponse", data => {
            if (data && data.processRecordId === processRecordId) {
                setShowModal(null)
                if (data.nextChecklistPeriod) {
                    navigate(`/checklist/${data.nextChecklistPeriod}`)
                }
            }
            unsubscribeUpdate()
        })
        sendMessageToWebsocket(payload)
    }


    const [showConfirmEndStage, setShowConfirmEndStage] = useState(false)
    const thisProcessData = processId && processData && processData[processId]
    const currentStageId = thisProcessData && thisProcessData['processRecord'] && thisProcessData['processRecord']['CurrentStageId']
    const thisStageDetails = thisProcessData && thisProcessData['Stages'] && currentStageId && thisProcessData['Stages'][currentStageId]
    const isOngoingStage = thisStageDetails && thisStageDetails['Behaviour'] === 'ongoing'
    const thisStageProcessRecord = thisProcessData && thisProcessData['processRecord'] && thisProcessData['processRecord']['StageProgress'][currentStageId]
    const nextChecksDue = thisStageProcessRecord && thisStageProcessRecord['NextRepetitionTime']
    const customNextSteps = thisProcessData && thisProcessData['NextSteps'] && Object.keys(thisProcessData['NextSteps']).length > 0

   


    if (!thisProcessData) {
        return <div className={`w-full flex-1 h-full flex flex-col items-center gap-2`}>
            <div className={`max-w-3xl w-full px-5 py-5 flex flex-col gap-4`}>
                <Spinner size='large'>{t('ProcessPages.common.loading')}</Spinner>
            </div>
        </div>
    }

    return <div className={`w-full flex-1 h-full flex flex-col items-center gap-2`}>


        {thisProcessData.InProgress === true && <div className={`w-full`}>
            <div className={`w-full flex flex-row gap-4 items-center bg-white justify-center arrow-white drop-shadow-xl border-b-2 border-gray-300`} >
                <div className={`max-w-3xl w-full px-5 py-2 flex flex-col gap-4 items-center justify-center text-center`}>

                    <ProcessStageIndicator
                        thisProcessData={thisProcessData}
                    />
                </div>
            </div>
        </div>}




        <div className={`
        ${thisProcessData.InProgress ? ' pt-8 pb-20 ' : ' py-10'}
        max-w-3xl w-full px-5 flex flex-col gap-4
            `}>

            {showConfirmEndStage && <div className={`w-full flex flex-col gap-6 items-center text-center`}>
                <h3 className={`text-2xl font-righteous text-brandblue`}>{t('ProcessPages.ProcessSummary.areYouSureProceed')}</h3>
                <p>{t('ProcessPages.ProcessSummary.movingToAnotherStage', { stageName: thisStageDetails && thisStageDetails['DisplayName'] })}</p>


                <div className={`w-full flex flex-col gap-2`}>
                    {!customNextSteps && <Button
                        onClick={(e: any) => { completeStageDefault(thisProcessData.processRecord.Id) }}
                        text={t('ProcessPages.ProcessSummary.confirm')}
                        fullwidth={true}
                        size={`big`}
                        variant={`primary`}
                    />}

                    {customNextSteps && Object.keys(thisProcessData.NextSteps).map((step_id: any, index: number) => {
                        const thisStep = thisProcessData.NextSteps[step_id]
                        return <Button
                            key={index}
                            onClick={(e: any) => { completeStageCustomNextStep(step_id, thisProcessData.processRecord.Id) }}
                            text={t('ProcessPages.ProcessSummary.nextStepName', { name: thisStep.NextStepName })}
                            fullwidth={true}
                            size={`big`}
                            variant={`primary`}
                        />
                    })}

                    <Button
                        onClick={(e: any) => { setShowConfirmEndStage(false) }}
                        text={t('ProcessPages.ProcessSummary.cancel')}
                        fullwidth={true}
                        size={`big`}
                        variant={`white`}
                    />
                </div>

            </div>}


            {!showConfirmEndStage && <div className={`w-full flex flex-col gap-6 items-center`}>


                {thisProcessData.InProgress === false && <StartProcess
                    thisProcessData={thisProcessData}
                    processId={processId}
                />}



                {nextChecksDue && <CountdownTimer timeToNextCheck={nextChecksDue} />}

                {thisProcessData.InProgress === true && <ProcessSummaryCheckPeriods />}


                {isOngoingStage && <FooterNavButtonFrame title={t('ProcessPages.ProcessSummary.nextSteps')}>
                    {!customNextSteps && <Button
                        onClick={(e: any) => setShowConfirmEndStage(true)}
                        text={t('ProcessPages.ProcessSummary.completeThisStage')}
                        fullwidth={true}
                        size={`big`}
                        variant={`primary`}
                    />}


                    {customNextSteps && Object.keys(thisProcessData.NextSteps).map((step_id: any, index: number) => {
                        const thisStep = thisProcessData.NextSteps[step_id]
                        return <Button
                            key={index}
                            onClick={(e: any) => setShowConfirmEndStage(true)}
                            text={t('ProcessPages.ProcessSummary.nextStepName', { name: thisStep.NextStepName })}
                            fullwidth={true}
                            size={`big`}
                            variant={`primary`}
                        />
                    })}
                </FooterNavButtonFrame>}
            </div>}
        </div>



    </div >

}
export default ProcessSummary